import { useCanonical } from '../../hooks/useCanonical';
import { Link } from 'react-router-dom';

/**
 * Public terms of service.
 *
 * The contracting party is the SITE, not the individual. An estate pays the
 * subscription and is the juristic person bound here; a patroller is that
 * site's employee, is handed a PIN by their own administrator, and never
 * contracts with us. Their consent to location collection is a separate
 * instrument — the prominent disclosure shown in the app before the OS
 * permission prompt — and these terms deliberately do not attempt to stand in
 * for it.
 *
 * Written to describe what the system ACTUALLY does. Section 4 exists because
 * the single most dangerous misunderstanding a customer could have about this
 * product is that someone is watching. Nobody is. It records, it notifies the
 * site's own people, and that is all. Do not soften that section to make the
 * product sound better than it is.
 *
 * TERMS_VERSION is stored against the estate at registration. Change it
 * whenever the substance changes, never for a typo fix — the version is what
 * makes "which terms did they accept" answerable later.
 */
export const TERMS_VERSION = '2026-08-22';
const UPDATED = '22 August 2026';

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{n}. {title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export default function Terms() {
  useCanonical('/terms');
  return (
    <div className="min-h-screen bg-white">
      <nav className="flex items-center justify-between px-8 py-5 border-b">
        <Link to="/" className="text-xl font-bold text-gray-900">PatrolIt</Link>
        <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">← Back</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-8 py-12 text-gray-700 leading-relaxed">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-400 mb-10">
          Last updated {UPDATED} &middot; Version {TERMS_VERSION}
        </p>

        <p className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded text-sm">
          These terms are a legal agreement between your site and Lenscap Studios.
          Please read section 4 in particular: it explains what PatrolIt does not do.
        </p>

        <Section n={1} title="These terms">
          <p>
            These terms govern use of PatrolIt (the &ldquo;Service&rdquo;). They form an
            agreement between <strong>Lenscap Studios</strong> (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;) and the estate, neighbourhood watch, business or other
            organisation that registers for the Service (the &ldquo;Site&rdquo;,
            &ldquo;you&rdquo;).
          </p>
          <p>
            The agreement is with the Site as an organisation, not with any individual.
            The person who registers confirms they are authorised to bind the Site.
          </p>
        </Section>

        <Section n={2} title="Who we are">
          <p>
            PatrolIt is operated by <strong>Lenscap Studios</strong>, a private company
            registered in South Africa, registration number <strong>K2023650167</strong>,
            registered office 6 The Old Road, Hout Bay, Cape Town, Western Cape, 7806.
          </p>
        </Section>

        <Section n={3} title="What the Service does">
          <p>
            PatrolIt records security patrols. A patroller signs in on an Android device
            with a name and PIN issued by your administrator, starts a patrol, and the
            app records their position while that patrol is running. Your control room
            sees active patrollers on a live map, receives incident and alarm
            notifications raised by patrollers, and can review completed patrols
            afterwards.
          </p>
          <p>
            When a patroller raises an alarm, the Service notifies your control room and
            emails the addresses your administrator has configured for your Site.
          </p>
        </Section>

        <Section n={4} title="What the Service is not">
          <p className="font-medium text-gray-900">
            PatrolIt is a recording and notification tool. It is not a security service,
            a monitoring service, or an emergency response service.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Nobody at Lenscap Studios watches your map.</strong> Alarms and
              incidents go to your own people and your own nominated email addresses.
              We do not monitor them, and we will not act on them.
            </li>
            <li>
              <strong>We do not dispatch anyone.</strong> The Service does not contact
              police, armed response, medical services or any third party on your behalf.
            </li>
            <li>
              <strong>An alarm raised in the app is not a call for help to us.</strong>
              In an emergency, call the appropriate emergency service directly.
            </li>
            <li>
              <strong>The Service can fail.</strong> Phones lose signal, batteries die,
              GPS is imprecise, Android may stop background apps, and networks and servers
              go down. A patrol that appears stationary, or an alarm that does not arrive,
              is possible.
            </li>
          </ul>
          <p>
            Do not rely on the Service as the only means by which anyone would learn that
            a patroller is in difficulty.
          </p>
        </Section>

        <Section n={5} title="Your patrollers">
          <p>
            The Service records the location of people working for you. That makes your
            Site responsible for the lawfulness of that recording under the Protection of
            Personal Information Act. We process this information on your instructions.
          </p>
          <p>By using the Service, you confirm that:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              you have a lawful basis to record the location of the people you add as
              patrollers;
            </li>
            <li>
              you have told them that their location is recorded while a patrol is
              running, and what it is used for;
            </li>
            <li>
              you will only add people who patrol for your Site, and you will remove them
              when they stop.
            </li>
          </ul>
          <p>
            The app also asks each patroller for permission before collecting location,
            and explains what is collected. That permission covers the device. It does not
            replace your own obligations as their employer.
          </p>
        </Section>

        <Section n={6} title="Accounts and access">
          <p>
            Your administrator creates patroller logins and portal users for your Site,
            and is responsible for who holds them. PINs and passwords are yours to manage;
            tell us promptly if you believe any have been compromised.
          </p>
          <p>
            Each Site's data is separate. Your patrols, incidents and alarms are not
            visible to any other Site.
          </p>
        </Section>

        <Section n={7} title="Subscription and payment">
          <p>
            The Service is provided on a monthly subscription at the fee shown at
            registration, collected by recurring card payment through PayFast. We do not
            see or store your card details.
          </p>
          <p>
            Access continues while the subscription is paid. If a payment fails and is not
            resolved, we may suspend access to the Service.
          </p>
        </Section>

        <Section n={8} title="Cancelling">
          <p>
            You may cancel at any time by asking us through the portal or by email.
            Cancellation stops future billing. We do not refund the portion of a month
            already paid.
          </p>
          <p>
            Cancelling does not by itself delete your recorded patrol history. If you want
            your data removed, ask us and we will do it.
          </p>
        </Section>

        <Section n={9} title="Your data">
          <p>
            Patrol tracks, incidents, alarms and the notes your patrollers write belong to
            your Site. We hold them in order to provide the Service to you.
          </p>
          <p>
            We do not sell your data and we do not use it for advertising. How personal
            information is handled is described in our{' '}
            <Link to="/privacy" className="text-teal-700 underline">Privacy Policy</Link>.
          </p>
        </Section>

        <Section n={10} title="Availability">
          <p>
            We aim to keep the Service running continuously, but we do not guarantee it.
            The Service may be unavailable for maintenance, or because of failures in
            hosting, networks, mobile devices or third parties we depend on.
          </p>
        </Section>

        <Section n={11} title="No warranties">
          <p>
            The Service is provided as it is. To the extent the law allows, we make no
            warranties that it will be uninterrupted, error free, or that recorded
            positions will be accurate or complete.
          </p>
        </Section>

        <Section n={12} title="Limitation of liability">
          <p>
            To the fullest extent permitted by law, we are not liable for any loss,
            damage, injury, theft or harm arising from your use of, or inability to use,
            the Service — including any incident the Service failed to record, any alarm
            that did not arrive, and any decision taken or not taken in reliance on it.
          </p>
          <p>
            Where liability cannot be excluded, our total liability to you for any claim
            is limited to the subscription fees you paid us in the three months before the
            claim arose.
          </p>
          <p>
            Nothing in these terms excludes liability that cannot lawfully be excluded,
            including liability for fraud or for death or personal injury caused by our
            negligence.
          </p>
        </Section>

        <Section n={13} title="Acceptable use">
          <p>
            Do not use the Service to track anyone who is not patrolling for your Site,
            to break the law, or to attempt to reach another Site's data. We may suspend
            or end access for a Site that does.
          </p>
        </Section>

        <Section n={14} title="Changes">
          <p>
            We may change the Service, and we may update these terms. If we change them in
            a way that materially affects you, we will tell the email address registered
            for your Site. Continuing to use the Service after that means you accept the
            updated terms.
          </p>
        </Section>

        <Section n={15} title="Ending the agreement">
          <p>
            You may stop using the Service at any time by cancelling. We may suspend or
            end access if a subscription goes unpaid, if these terms are breached, or if
            we stop offering the Service — in which case we will give you reasonable
            notice and an opportunity to obtain your data.
          </p>
        </Section>

        <Section n={16} title="Governing law">
          <p>
            These terms are governed by the laws of the Republic of South Africa, and the
            South African courts have jurisdiction.
          </p>
        </Section>

        <Section n={17} title="Contact">
          <p>
            Lenscap Studios, 6 The Old Road, Hout Bay, Cape Town, Western Cape, 7806.
            Email <a href="mailto:contact@patrolit.org" className="text-teal-700 underline">contact@patrolit.org</a>.
          </p>
        </Section>

        <p className="mt-12 pt-6 border-t text-sm text-gray-400">
          <Link to="/" className="underline">Back to home</Link>
          {' · '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </main>
    </div>
  );
}
